import * as React from 'react';
import {Box, Button, ButtonProps, SxProps, Theme, Dialog, DialogTitle, DialogContent, 
  Typography, IconButton, DialogActions, RadioGroup, FormControlLabel, Radio, useTheme} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useAppSelector } from '../../../app/hooks';
import { isPlayerLocked } from '../../util/PlayerDataUtil';
import { League, Player, Contract, Team, AutoSwapInfo } from '../../../sdk/model';
import { teamApi } from '../../../adapters/APIExporter';
import { getBenchData, getFilteredBenchData } from '../../util/RosterUtils';
import { isPlayerLockedAt } from '../../util/PlayerDataUtil';
import PlayerCardLink from '../../cards/playerCardLink';
import { positionSorter } from '../../util/sortingUtils';
import { positionOrderNoBench } from '../../util/PositionOrder';
import playerMatchupDisplay from '../../util/PlayerMatchupDisplay';
import { MaterialReactTable } from 'material-react-table';
import { ExpandableRow } from './roster/ExpandableRow';

interface SetAutoSubButtonProps extends Omit<ButtonProps, 'onClick' | 'disabled' | 'sx'> {
  league: League;
  player: Player;
  position: string;
  contract: Contract;
  currentSeasonYear: number;
  team: Team;
  playerMap: Map<string | undefined, Player | undefined>
  sx?: SxProps<Theme>;
  callback: (result: any) => void;
}

const SetAutoSubButton: React.FC<SetAutoSubButtonProps> = ({
  league,
  player,
  position,
  contract,
  currentSeasonYear,
  team,
  playerMap,
  callback,
  sx = {},
  ...buttonProps
}) => {
  const buttonDisabled = isPlayerLocked(contract);
  const [choosing, setChoosing] = React.useState(false);
  const [selectedPlayer, setSelectedPlayer] = React.useState<string | undefined>(undefined);
  const [errors, setErrors] = React.useState<string[]>([]);
  
  const theme = useTheme();
  
  const setAutoSub = async () => {
    const autoSwapInfo: AutoSwapInfo = {
      startingPlayerId: player.id,
      benchPlayerId: selectedPlayer
    }
    teamApi.setAutoSwap(team.id, autoSwapInfo).then(response => {
      callback(response.data);
      setChoosing(false);
    }).catch(error => {
      setErrors([error.response.data.message]);
    });
  };
  
  const nflGames = useAppSelector((state) => state.nflGameDataLoad);
  const nflGameMap = new Map(nflGames.map(nflGame => [nflGame.id!, nflGame]));
  
  let columns = 
      [
        {
          accessorKey: 'player.id',
          id: 'select',
          header: 'Select',
          minSize: 15,
          size: 15,
          enableSorting: false,
          Cell: ({ renderedCellValue, row }) => {
            return <FormControlLabel value={renderedCellValue || 'NONE'} control={<Radio />} disabled={!row.original.selectable} label="" />
          }
        },
        {
          accessorKey: 'player',
          id: 'player',
          header: 'Player',
          minSize: 15,
          size: 15,
          Cell: ({ renderedCellValue, row }) => {
            const player = renderedCellValue;
            return player ? <PlayerCardLink player={player} leagueId={league.id!} /> : 'Empty';
          },
          sortingFn: positionSorter(positionOrderNoBench)
        },
        {
          accessorKey: 'player',
          id: 'currentMatchup',
          header: 'Current Matchup',
          minSize: 15,
          size: 15,
          Cell: ({ renderedCellValue }) => {
            const player : Player | undefined = renderedCellValue;
            const currentWeekStats = player?.quick_stats?.currentWeeksStats;
            const nflGame = currentWeekStats ? nflGameMap.get(currentWeekStats.gameId!) : undefined;
            return player && player.nfl_team ? playerMatchupDisplay(currentWeekStats, nflGame) : 'N/A'
          }
        },
        {
          accessorKey: 'projection',
          id: 'projection',
          header: 'Projected Score',
          minSize: 15,
          size: 15,
          Cell: ({ renderedCellValue }) => 
            renderedCellValue ? renderedCellValue.toFixed(2) : "0.00"
          
        }
      ];
  
  let benchData = getBenchData(team.playerRoster!, [], [], new Map(), currentSeasonYear, playerMap, league);
  benchData = getFilteredBenchData(benchData, position, player, team.playerRoster!, playerMap, [], currentSeasonYear, league!);
  benchData.forEach(benchPlayer => {
    const startDate = new Date(player.quick_stats?.currentWeeksStats?.startDateTime!);
    startDate.setSeconds(startDate.getSeconds() + 1);
    benchPlayer.selectable = !benchPlayer.player || !!benchPlayer.player?.quick_stats?.currentWeeksStats?.startDateTime && !isPlayerLockedAt(benchPlayer.player, startDate);
  });
  
  return (
    <Box>
      <Button
        variant="contained"
        color="secondary"
        onClick={() => setChoosing(true)}
        disabled={buttonDisabled}
        fullWidth={buttonProps.fullWidth || false}
        sx={sx}
        {...buttonProps}
      >
        Set Auto-Sub
      </Button>
      <Dialog 
        open={choosing} 
        onClose={() => setChoosing(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Choose player to auto-sub if {player.abbr_name} doesn't play:</Typography>
            <IconButton 
              onClick={() => setChoosing(false)} 
              size="large" 
              edge="end"
              aria-label="close"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
        {errors.length > 0 && (
          <Box color="error.main" mt={2}>
            {errors.map((error, index) => (
              <Typography key={index} color="error">
                {error}
              </Typography>
            ))}
          </Box>
        )}
        <RadioGroup 
          value={selectedPlayer} 
          name="selectedPlayer" 
          onChange={(e) => {
            // Only update if the value is different to prevent unnecessary re-renders
            if (e.target.value !== selectedPlayer) {
              setSelectedPlayer(e.target.value || 'NONE');
            }
          }}
          sx={{ display: 'block' }}
        >
          <MaterialReactTable
              columns={columns}
              data={benchData}
              getRowId={(row) => row.id!}
              enableColumnActions={true}
              enableSorting={true}
              enableFilters={false}
              enablePagination={false}
              enableGrouping
              // groupedColumnMode="remove"
              enableStickyHeader={false}
              enableStickyFooter={false}
              sortingFns={{
                position: positionSorter(positionOrderNoBench)
              }}  
              state={{
                grouping: ['position'],
                expanded: true,
                sorting: [
                  { id: 'position', desc: false }
                ]
              }}
              muiTableContainerProps={{
                sx: { 
                  '&::-webkit-scrollbar': {
                    width: '8px',
                    height: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: theme.palette.mode === 'light' ? '#f1f1f1' : '#424242',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: theme.palette.mode === 'light' ? '#888' : '#666',
                    borderRadius: '4px',
                    '&:hover': {
                      background: theme.palette.mode === 'light' ? '#777' : '#888',
                    },
                  },
                }
              }}
              muiTableHeadCellProps={{
                sx: {
                  "& .Mui-TableHeadCell-Content-Labels": {
                    padding: "0px",
                  },
                  "& .MuiBox-root": {
                    padding: "0px",
                  },
                },
              }}
              muiTableBodyRowProps={({ row }) => ({
                component: 'tr',
                children: (
                  <ExpandableRow
                    row={row.original}
                    columns={columns}
                    isMobile={false}
                    selectedPosition={undefined}
                    getSwapOption={undefined}
                    roster={team.playerRoster}
                    playerMap={playerMap}
                  />
                )
              })}
              displayColumnDefOptions={{
                'mrt-row-expand': {
                  enableResizing: false,
                  size: 60,
                },
              }}
              enableColumnResizing={false}
              enableColumnDragging={false}
              enableFullScreenToggle={false}
              enableDensityToggle={false}
              enableHiding={false}
              enableTopToolbar={false}
            />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setChoosing(false)} 
            color="primary"
          >
            Cancel
          </Button>
          <Button 
            onClick={setAutoSub} 
            disabled={!selectedPlayer}
            color="primary" 
            variant="contained"
          >
            Set Auto-Sub
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
};

export default SetAutoSubButton;